import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Image as ImageIcon,
  Compress as CompressIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ImageCompression, CompressionResult } from '../../utils/imageCompression';

interface ImagePreviewPopupProps {
  open: boolean;
  onClose: () => void;
  onSend: (altText: string, compressedFile: File) => void;
  imageFile: File | null;
  imagePreview: string | null;
  loading: boolean;
  error: string | null;
}

const ImagePreviewPopup: React.FC<ImagePreviewPopupProps> = ({
  open,
  onClose,
  onSend,
  imageFile,
  imagePreview,
  loading,
  error,
}) => {
  const [altText, setAltText] = useState('');
  const [compressionResult, setCompressionResult] = useState<CompressionResult | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionError, setCompressionError] = useState<string | null>(null);
  const [compressedPreview, setCompressedPreview] = useState<string | null>(null);
  const [showCompressionInfo, setShowCompressionInfo] = useState(false);

  // Reset state when popup opens
  useEffect(() => {
    if (open && imageFile) {
      setAltText('');
      setCompressionResult(null);
      setCompressionError(null);
      setCompressedPreview(null);
      setShowCompressionInfo(false);
      compressImage();
    }
  }, [open, imageFile]);

  const compressImage = async () => {
    if (!imageFile) return;

    setCompressing(true);
    setCompressionError(null);

    try {
      // Check if it's a supported image type
      if (!ImageCompression.isSupportedImageType(imageFile)) {
        throw new Error('Unsupported image format. Please use JPEG, PNG, or WebP.');
      }

      // Get compression settings based on file size
      const compressionOptions = ImageCompression.getCompressionSettings(imageFile);
      
      // Compress the image
      const result = await ImageCompression.compressImage(imageFile, compressionOptions);
      
      setCompressionResult(result);
      
      // Create preview URL for compressed image
      const compressedPreviewUrl = URL.createObjectURL(result.compressedFile);
      setCompressedPreview(compressedPreviewUrl);
      
    } catch (error) {
      console.error('Compression error:', error);
      setCompressionError(error instanceof Error ? error.message : 'Failed to compress image');
    } finally {
      setCompressing(false);
    }
  };

  const handleSend = () => {
    if (compressionResult) {
      onSend(altText, compressionResult.compressedFile);
    }
  };

  const getCompressionQualityColor = (ratio: number): string => {
    if (ratio > 70) return 'success';
    if (ratio > 40) return 'warning';
    return 'error';
  };

  const getCompressionQualityText = (ratio: number): string => {
    if (ratio > 70) return 'Excellent';
    if (ratio > 40) return 'Good';
    if (ratio > 20) return 'Fair';
    return 'Poor';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CompressIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ 
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Send Image
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {compressionResult && (
                <Button
                  onClick={() => setShowCompressionInfo(!showCompressionInfo)}
                  variant="outlined"
                  size="small"
                  startIcon={showCompressionInfo ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  sx={{ 
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  {showCompressionInfo ? 'Hide Details' : 'Show Compression Info'}
                </Button>
              )}
              <Button
                onClick={onClose}
                variant="outlined"
                size="small"
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                <CloseIcon />
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, background: 'rgba(255, 255, 255, 0.6)' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {compressionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {compressionError}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 4,
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography variant="body1">
                Uploading image...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Compression Progress */}
              {compressing && (
                <Box sx={{ 
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body1" fontWeight="bold">
                      Compressing image...
                    </Typography>
                  </Box>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                </Box>
              )}

              {/* Simplified View (Default) */}
              {compressionResult && !showCompressionInfo && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}>
                  {/* Compressed Image Preview */}
                  {compressedPreview && (
                    <Box sx={{ 
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                      border: '2px solid rgba(25, 118, 210, 0.2)'
                    }}>
                      <img
                        src={compressedPreview}
                        alt="Compressed preview"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: 400,
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  )}

                  {/* Quick Compression Info */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`${compressionResult.compressionRatio.toFixed(0)}% smaller`}
                      color={getCompressionQualityColor(compressionResult.compressionRatio) as any}
                      size="medium"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {ImageCompression.formatFileSize(compressionResult.originalSize)} → {ImageCompression.formatFileSize(compressionResult.compressedSize)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Detailed Compression View */}
              {compressionResult && showCompressionInfo && (
                <>
                  {/* Image Comparison */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    {/* Original Image */}
                    <Box sx={{ flex: 1 }}>
                      <Card sx={{ 
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
                            Original Image
                          </Typography>
                          {imagePreview && (
                            <Box sx={{ 
                              maxHeight: 200,
                              borderRadius: 1,
                              overflow: 'hidden',
                              mb: 2,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}>
                              <img
                                src={imagePreview}
                                alt="Original"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: 200,
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2">
                              <strong>Size:</strong> {ImageCompression.formatFileSize(compressionResult.originalSize)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Dimensions:</strong> {compressionResult.dimensions.original.width} × {compressionResult.dimensions.original.height}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>

                    {/* Compressed Image */}
                    <Box sx={{ flex: 1 }}>
                      <Card sx={{ 
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold" color="text.secondary">
                              Compressed Image
                            </Typography>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={getCompressionQualityText(compressionResult.compressionRatio)}
                              color={getCompressionQualityColor(compressionResult.compressionRatio) as any}
                              size="small"
                            />
                          </Box>
                          {compressedPreview && (
                            <Box sx={{ 
                              maxHeight: 200,
                              borderRadius: 1,
                              overflow: 'hidden',
                              mb: 2,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}>
                              <img
                                src={compressedPreview}
                                alt="Compressed"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: 200,
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2">
                              <strong>Size:</strong> {ImageCompression.formatFileSize(compressionResult.compressedSize)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Dimensions:</strong> {compressionResult.dimensions.compressed.width} × {compressionResult.dimensions.compressed.height}
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">
                              <strong>Saved:</strong> {compressionResult.compressionRatio.toFixed(1)}% ({ImageCompression.formatFileSize(compressionResult.originalSize - compressionResult.compressedSize)})
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>

                  {/* Compression Stats */}
                  <Paper sx={{ 
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                      Compression Statistics
                    </Typography>
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                      gap: 2 
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                          {compressionResult.compressionRatio.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Size Reduction
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {(compressionResult.originalSize / compressionResult.compressedSize).toFixed(1)}x
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Compression Ratio
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main" fontWeight="bold">
                          {compressionResult.quality * 100}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Quality Level
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                          {ImageCompression.formatFileSize(compressionResult.originalSize - compressionResult.compressedSize)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Space Saved
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </>
              )}

              {/* Alt Text Input */}
              <TextField
                label="Alt Text (Optional)"
                placeholder="Describe the image for accessibility..."
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                multiline
                rows={2}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                  }
                }}
                helperText="Alt text helps screen readers describe the image to users with visual impairments"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={loading || !compressionResult || compressing}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Sending...' : 'Send Compressed Image'}
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default ImagePreviewPopup;